import {createSelector} from 'reselect';

import {BARCODE} from '../../../apps/sellTicket/config';
import {ALPHANUMERIC_INPUT_PATTERN_LENGTH_2, NUMERIC_INPUT_PATTERN_LENGTH_3} from '../../config';
import {deliverySelector} from '../withDelivery/selectors';
import {
  filterByEventId,
  isGAEventSelector,
  isSeatsRequiredSelector,
  isSRSRequiredSelector,
  selectEventOriginCountry
} from '../withEvents/selectors';

import {NAMESPACE} from './index';

const bannedPartialTerms = ['platea', 'front', 'swallows', 'stand', 'Ótimo', 'gold', 'tobc1', 'ga', 'platino', '5x',
  'n.a', 'top', 'poltriniss', '1x', 'n/a', 'undecided', 'good', 'recht', 'tba', 'na', 'unreserved',
  'popul', 'reihe', 'tbc', 'plat', 'vip', 'innen', 'reihe c', 'tbd', 'tobeconf', 'visitor', 'libre', 'seiten',
  'center', 'student', 'arena', 'mitte', 'grada', 'suite', 'free', 'neben', 'steh', 'stehplatz',
  'bassi', 'debout', 'entrada', 'best', 'be$t', '+', '?', '!', 'unknown', 'none', 'pit', 'seated',
  'together', 'confirmed', 'tc', 'tobeconfirmed', 'tob', 'tbt', 'nice', 'ticket', 'tic'];

const bannedExactTerms = ['00', '000'];

export const checkTicketForSeatRowError = (seatRowValue) => {
  const seatRegex = NUMERIC_INPUT_PATTERN_LENGTH_3;

  return (
    seatRowValue === null ||
    typeof seatRowValue === 'undefined' ||
    seatRowValue.trim() === '' ||
    !RegExp(seatRegex).test(seatRowValue) ||
    bannedPartialTerms.some((term) => seatRowValue.toLowerCase().indexOf(term) > -1) ||
    bannedExactTerms.some((bannedTerm) => {
      return bannedTerm === seatRowValue.trim();
    })
  );
};

export const checkTicketForRowError = (rowValue) => {
  const rowRegex = ALPHANUMERIC_INPUT_PATTERN_LENGTH_2;

  return (
    rowValue === null ||
    typeof rowValue === 'undefined' ||
    rowValue.trim() === '' ||
    !RegExp(rowRegex).test(rowValue) ||
    bannedPartialTerms.some((term) => rowValue.toLowerCase().indexOf(term) > -1) ||
    bannedExactTerms.some((bannedTerm) => {
      return bannedTerm === rowValue.trim();
    })
  );
};

export const ticketSelector = createSelector(
  (state) => state[NAMESPACE],
  (tickets) => tickets || {}
);
export const ticketCountSelector = createSelector(
  ticketSelector,
  ({master}) => master?.length || 0
);
export const ticketSelectorForEvent = (state, eventId) => (eventId ?
  filterByEventId(ticketSelector(state)?.tickets, eventId) :
  ticketSelector(state)?.tickets
);
export const sectionRowSelector = createSelector(
  ticketSelector,
  ({section, row, tickets = []}) => ({
    section: section || tickets[0]?.section,
    row: row || tickets[0]?.row
  })
);
export const rowErrorSelector = createSelector(
  ticketSelector,
  (tickets) => tickets.rowError
);

export const isGASectionSelector = createSelector(
  sectionRowSelector,
  ({section}) => section?.generalAdmission
);
export const enterLaterSelectedSelector = createSelector(
  ticketSelector,
  (tickets) => !tickets?.seatsValueRequired
);

// TODO: replace these
export const isEnteringBarcodesNowSelector = createSelector(
  deliverySelector,
  ({events = []} = {}) => events.length && events?.some((event) => event.deliverNow && event.fulfillmentMethod?.ticketType === BARCODE)
);

export const ticketsSectionRowSeatsValid = createSelector([
  ticketSelector,
  rowErrorSelector,
  isGAEventSelector,
  isGASectionSelector,
  isEnteringBarcodesNowSelector,
  isSRSRequiredSelector,
  isSeatsRequiredSelector,
  enterLaterSelectedSelector,
  selectEventOriginCountry
], validateTicketsSectionRowSeats);

export function validateTicketsSectionRowSeats(
  {tickets = []} = {},
  rowError,
  isGAEvent,
  isGASection,
  isEnteringBarcodesNow,
  isSRSRequired,
  isSeatsRequired,
  enterLaterSelected,
  eventOriginCountry
) {
  if (!tickets.length) {
    return false;
  }

  if (isGAEvent) {
    return true;
  }

  return tickets.every((ticket) =>
    validTicket(ticket, {
      isGASection,
      isSRSRequired,
      isSeatsRequired,
      isEnteringBarcodesNow,
      enterLaterSelected,
      eventOriginCountry,
      rowError
    })
  );
}

export const validTicket = (ticket, {
  isGASection,
  isSRSRequired,
  isSeatsRequired,
  isEnteringBarcodesNow,
  enterLaterSelected,
  eventOriginCountry,
  rowError
}) => {
  if (!(ticket.section?.id || ticket.section?.name) && (!isGASection || isSRSRequired)) {
    return false;
  }

  if (!ticket.row && isSRSRequired) {
    return false;
  }

  if (!ticket.row && !isGASection) {
    return false;
  }

  if (!ticket.seat && isSeatsRequired) {
    return false;
  }

  if (isGASection) {
    return true;
  }

  if (!isEnteringBarcodesNow && !ticket.seat && !enterLaterSelected) {
    return false;
  }

  if (ticket.isDuplicate) {
    return false;
  }

  if (isEnteringBarcodesNow && !ticket.isValid) {
    return false;
  }

  if (eventOriginCountry === 'GB' && (rowError || ticket.seatError)) {
    return false;
  }

  return true;
};
